import React, { useState } from 'react';
import { CoachingChatbot } from '../components/CoachingChatbot';
import { ChatBubbleLeftRightIcon, InformationCircleIcon, UserIcon } from '@heroicons/react/24/outline';

interface ChatbotTestPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
  clientName?: string;
}

export const ChatbotTestPage: React.FC<ChatbotTestPageProps> = ({
  user,
  coachMode = false,
  clientId,
  clientName
}) => {
  const [showInfo, setShowInfo] = useState(true);

  // Determine which userId to use: client's (in coach mode) or user's own
  const effectiveUserId = coachMode && clientId ? String(clientId) : String(user?.id || '');
  const displayName = coachMode && clientName ? clientName : (user?.full_name || user?.email);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ChatBubbleLeftRightIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {coachMode ? 'Client Chatbot Conversations' : 'AI Coaching Chatbot'}
            </h1>
            <p className="text-sm text-gray-600">
              {coachMode
                ? 'Review your client\'s AI coaching conversations'
                : 'Your personal AI nutrition coaching assistant'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
        >
          <InformationCircleIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Client Info Banner (Coach Mode) */}
      {coachMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <UserIcon className="w-6 h-6 text-blue-600" />
          <div>
            <p className="text-sm font-semibold text-blue-900">
              Viewing conversations for: {displayName || 'No client selected'}
            </p>
            <p className="text-xs text-blue-700">
              {clientId
                ? 'All conversations between this client and the AI coach are shown below'
                : 'Select a client from the sidebar to view their chatbot conversations'}
            </p>
          </div>
        </div>
      )}

      {/* Info Panel */}
      {showInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            {coachMode ? 'About Client Chatbot Review' : 'About This Feature'}
          </h3>
          <div className="text-sm text-blue-800 space-y-2">
            {coachMode ? (
              <>
                <p>
                  <strong>Purpose:</strong> This view allows you to review all AI coaching conversations
                  your client has had with the chatbot system.
                </p>
                <p>
                  <strong>What clients can do:</strong>
                </p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Ask nutrition questions and get personalized AI coaching</li>
                  <li>Receive meal analysis based on their tracked food</li>
                  <li>Get educational content recommendations</li>
                  <li>Build a searchable conversation history</li>
                </ul>
                <p>
                  <strong>Coach capabilities:</strong> You can view conversation history to understand
                  client concerns, questions, and coaching needs. This helps inform your personalized coaching strategy.
                </p>
              </>
            ) : (
              <>
                <p>
                  <strong>Purpose:</strong> Get personalized nutrition coaching powered by AI,
                  based on your tracked meals and health goals.
                </p>
                <p>
                  <strong>Key Features:</strong>
                </p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Personalized nutrition coaching based on your meal history</li>
                  <li>Educational content recommendations from knowledge base</li>
                  <li>Conversation history storage for reference</li>
                  <li>Integration with nutrient databases for accurate guidance</li>
                </ul>
              </>
            )}
            <div className="mt-3 p-2 bg-white border border-blue-200 rounded">
              <p className="text-blue-800 text-xs">
                <strong>Technical:</strong> Powered by Groq GPT OSS 20B with ChromaDB + Neo4j knowledge bases.
                Analyzes last 7 days of meal data and personalized targets.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* No Client Selected (Coach Mode) */}
      {coachMode && !clientId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <UserIcon className="w-16 h-16 mx-auto text-yellow-600 opacity-50 mb-4" />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">No Client Selected</h3>
          <p className="text-sm text-yellow-800">
            Please select a client from the sidebar to view their AI coaching conversations.
          </p>
        </div>
      )}

      {/* Chatbot Container */}
      {(!coachMode || clientId) && (
        <div className="bg-white rounded-lg shadow-lg" style={{ height: '600px' }}>
          {effectiveUserId ? (
            <CoachingChatbot userId={effectiveUserId} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <ChatBubbleLeftRightIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Unable to load chatbot - user ID not available</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Technical Details */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Technical Details</h3>
        <div className="text-xs text-gray-600 space-y-1">
          <p><strong>Backend Endpoint:</strong> POST /api/coach/chat</p>
          <p><strong>Database:</strong> coaching_conversations table</p>
          <p><strong>AI Model:</strong> Groq GPT OSS 20B (3x faster than previous model)</p>
          <p><strong>Knowledge Sources:</strong> ChromaDB (nutritional education) + Neo4j (nutrient relationships)</p>
          <p><strong>Context:</strong> Last 7 days of meal data + personalized targets</p>
          {coachMode && <p><strong>Viewing Mode:</strong> Coach review (client ID: {clientId || 'none'})</p>}
        </div>
      </div>
    </div>
  );
};

export default ChatbotTestPage;
